import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { fetchRegions } from '../lib/api';
import { REGION_BLURBS, REGION_ORDER } from '../lib/regionMeta';
import { applySeo, resetSeo } from '../lib/seo';
import JsonLd from '../components/JsonLd';

const BASE = 'https://marinelink.co.nz';

interface RegionCount {
  name: string;
  count: number;
}

export default function Regions() {
  const [regions, setRegions] = useState<RegionCount[]>([]);

  useEffect(() => {
    fetchRegions().then(setRegions).catch(() => setRegions([]));
  }, []);

  useEffect(() => {
    applySeo({
      title: 'NZ Marine Directory by Region | MarineLink',
      description: 'Browse New Zealand marine businesses by region. Marine businesses, charters, marinas and services across Auckland, Northland, Waikato, Wellington, Canterbury, Otago and every NZ region.',
      descriptionPad: 'Find marine businesses near you on MarineLink, New Zealand\'s marine industry directory.',
      path: '/regions',
      keywords: 'NZ marine directory, marine businesses by region, boat charters Auckland, marine services NZ, marinas New Zealand, marine directory NZ',
    });
    return () => resetSeo();
  }, []);

  // Order: curated editorial order first, then any DB regions not in the curated list.
  const counts = new Map(regions.map((r) => [r.name, r.count]));
  const curated = REGION_ORDER.filter((name) => counts.has(name));
  const extras = regions.map((r) => r.name).filter((name) => !REGION_ORDER.includes(name));
  const ordered = [...curated, ...extras];

  const itemListSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'New Zealand marine businesses by region',
    numberOfItems: ordered.length,
    itemListElement: ordered.map((name, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: `Marine businesses in ${name}`,
      url: `${BASE}/browse?region=${encodeURIComponent(name)}`,
    })),
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${BASE}/` },
      { '@type': 'ListItem', position: 2, name: 'Regions', item: `${BASE}/regions` },
    ],
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'Which NZ regions does MarineLink cover?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'MarineLink covers all New Zealand regions, including Auckland, Northland, Waikato, Bay of Plenty, Gisborne, Hawke\'s Bay, Taranaki, Manawatū-Whanganui, Wellington, Nelson, Marlborough, Canterbury, West Coast, Otago and Southland.',
        },
      },
      {
        '@type': 'Question',
        name: 'How do I find marine businesses in my region?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Choose your region on this page to see all marine businesses MarineLink lists there, or use the search and filters on the main browse page.',
        },
      },
    ],
  };

  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-8 py-8 md:py-12">
      <JsonLd id="regions-hub" data={[breadcrumbSchema, itemListSchema, faqSchema]} />

      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-sm text-text-muted-light dark:text-text-muted mb-6">
        <Link to="/" className="text-text-secondary-light dark:text-text-secondary hover:text-accent transition-colors">Home</Link>
        <span>/</span>
        <span className="text-text-primary-light dark:text-text-primary">Regions</span>
      </div>

      {/* Hero */}
      <div className="mb-10 max-w-3xl">
        <h1 className="font-display text-3xl md:text-4xl font-extrabold text-text-primary-light dark:text-text-primary mb-4">
          New Zealand marine businesses <span className="text-text-muted-light dark:text-text-muted font-medium">by region</span>
        </h1>
        <p className="text-text-secondary-light dark:text-text-secondary text-lg leading-relaxed">
          Every listing on MarineLink is tied to a real NZ region. Pick yours to see charters, marinas, boat builders,
          engineers and services operating locally — contact details included, no middleman.
        </p>
      </div>

      {/* Region grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
        {ordered.map((name) => {
          const count = counts.get(name) ?? 0;
          return (
            <Link
              key={name}
              to={`/browse?region=${encodeURIComponent(name)}`}
              className="group border border-[rgba(16,42,66,0.12)] dark:border-[rgba(214,233,247,0.12)] rounded-lg bg-surface-light dark:bg-surface p-5 hover:border-accent transition-colors flex flex-col"
            >
              <div className="flex items-baseline justify-between gap-3 mb-2">
                <span className="font-display text-lg font-bold text-text-primary-light dark:text-text-primary group-hover:text-accent transition-colors">{name}</span>
                <span className="text-xs text-text-muted whitespace-nowrap">{count} listing{count === 1 ? '' : 's'}</span>
              </div>
              <p className="text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
                {REGION_BLURBS[name] || `Marine businesses operating in ${name}, New Zealand.`}
              </p>
            </Link>
          );
        })}
      </div>

      {/* FAQ */}
      <section className="max-w-3xl" aria-labelledby="regions-faq-heading">
        <h2 id="regions-faq-heading" className="font-display text-2xl md:text-3xl font-bold text-text-primary-light dark:text-text-primary mb-2">
          NZ marine directory: region FAQs
        </h2>
        <p className="text-text-muted-light dark:text-text-muted mb-6 text-sm">
          Common questions about coverage across New Zealand's regions.
        </p>
        <div className="space-y-3">
          <details className="group border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface overflow-hidden">
            <summary className="cursor-pointer list-none px-5 py-4 font-medium text-text-primary-light dark:text-text-primary flex items-center justify-between gap-4 hover:text-accent transition-colors">
              <span>Which NZ regions does MarineLink cover?</span>
              <span className="text-text-muted-light dark:text-text-muted group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
            </summary>
            <div className="px-5 pb-4 text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              MarineLink covers all New Zealand regions, including Auckland, Northland, Waikato, Bay of Plenty, Gisborne, Hawke's Bay, Taranaki, Manawatū-Whanganui, Wellington, Nelson, Marlborough, Canterbury, West Coast, Otago and Southland.
            </div>
          </details>
          <details className="group border border-[rgba(16,42,66,0.14)] dark:border-[rgba(214,233,247,0.14)] rounded-lg bg-surface-light dark:bg-surface overflow-hidden">
            <summary className="cursor-pointer list-none px-5 py-4 font-medium text-text-primary-light dark:text-text-primary flex items-center justify-between gap-4 hover:text-accent transition-colors">
              <span>How do I find marine businesses in my region?</span>
              <span className="text-text-muted-light dark:text-text-muted group-open:rotate-45 transition-transform text-xl leading-none shrink-0">+</span>
            </summary>
            <div className="px-5 pb-4 text-sm text-text-secondary-light dark:text-text-secondary leading-relaxed">
              Choose your region above to see every marine business MarineLink lists there, or use the search and filters on the main browse page.
            </div>
          </details>
        </div>
      </section>
    </div>
  );
}
